import { Cloud, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { StatCard, LoaderBlock, fmtUSDCents } from './shared';
import { useObsAwsCosts } from '@/hooks/useObservability';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const SERVICE_COLORS: Record<string, string> = {
  'AWS Lambda':                          '#6366f1',
  'Amazon DynamoDB':                     '#8b5cf6',
  'Amazon Bedrock':                      '#0ea5e9',
  'Amazon API Gateway':                  '#10b981',
  'Amazon CloudFront':                   '#f59e0b',
  'Amazon Simple Storage Service (S3)':  '#f97316',
  'AWS Secrets Manager':                 '#ec4899',
  'Amazon Relational Database Service':  '#14b8a6',
  'Amazon Simple Notification Service':  '#a78bfa',
};

function barColor(service: string, i: number) {
  return SERVICE_COLORS[service] ?? `hsl(${(i * 47) % 360}, 60%, 55%)`;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
      <p className="font-medium">{payload[0]?.payload?.service}</p>
      <p className="text-muted-foreground">{fmtUSDCents(payload[0]?.value)}</p>
    </div>
  );
};

export function ObsAwsTab() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useObsAwsCosts();

  if (isLoading) return <LoaderBlock />;

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-amber-600">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium text-sm">{t('observability.aws.error_title')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('observability.aws.error_permission')}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const costs = data?.costs ?? [];
  const periodStart = data?.periodStart ?? '';
  const periodEnd = data?.periodEnd ?? '';
  const total = costs.reduce((s, c) => s + c.cost, 0);
  const top10 = costs.slice(0, 10);

  const chartData = top10.map((c) => ({
    service: c.service.replace('Amazon ', '').replace(' Service', '').replace('Amazon Web Services', 'AWS'),
    fullService: c.service,
    cost: Math.round(c.cost * 100) / 100,
  }));

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          icon={Cloud}
          label={t('observability.aws.stat_total')}
          value={fmtUSDCents(total)}
          sub={periodStart && periodEnd ? `${periodStart} → ${periodEnd}` : undefined}
          tone="blue"
        />
        <StatCard
          icon={Cloud}
          label={t('observability.aws.stat_services')}
          value={String(costs.length)}
          sub={t('observability.aws.stat_services_sub')}
          tone="purple"
        />
        <StatCard
          icon={Cloud}
          label={t('observability.aws.stat_top')}
          value={costs[0]?.service?.replace('Amazon ', '').split('(')[0].trim() ?? '—'}
          sub={costs[0] ? fmtUSDCents(costs[0].cost) : undefined}
          tone="amber"
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {t('observability.aws.estimation_note')}
      </p>

      {/* Bar chart */}
      {chartData.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-4">{t('observability.aws.top_services_chart')}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 40, top: 0, bottom: 0 }}>
              <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="service" width={120} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={barColor(entry.fullService, i)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Full table */}
      <Card>
        <div className="px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">{t('observability.aws.all_services_table')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-xs text-muted-foreground uppercase tracking-wide">
                <th className="px-4 py-2 text-left">{t('observability.aws.col_service')}</th>
                <th className="px-4 py-2 text-left text-xs normal-case font-normal">{t('observability.aws.col_detail')}</th>
                <th className="px-4 py-2 text-right">{t('observability.aws.col_cost')}</th>
                <th className="px-4 py-2 text-right">{t('observability.aws.col_pct')}</th>
                <th className="px-4 py-2 text-right w-32"></th>
              </tr>
            </thead>
            <tbody>
              {costs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground text-sm">{t('observability.aws.no_data')}</td>
                </tr>
              ) : costs.map((c, i) => {
                const pct = total > 0 ? (c.cost / total) * 100 : 0;
                return (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-2.5 text-sm font-medium">{c.service}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{(c as any).detail ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fmtUSDCents(c.cost)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{pct.toFixed(1)}%</td>
                    <td className="px-4 py-2.5 pr-4">
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full bg-indigo-500"
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
